package com.applaunchkit.website.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.applaunchkit.website.style.TextSelectionStyle
import com.applaunchkit.website.util.BackgroundGradient
import com.applaunchkit.website.util.Constants.ACME_REGULAR
import com.applaunchkit.website.util.Constants.LATO_LIGHT
import com.applaunchkit.website.util.Constants.LATO_REGULAR
import com.applaunchkit.website.util.Constants.SUPPORT_EMAIL
import com.applaunchkit.website.util.TextGradient
import com.applaunchkit.website.util.Theme
import com.varabyte.kobweb.compose.css.BackgroundClip
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.background
import com.varabyte.kobweb.compose.ui.modifiers.backgroundImage
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@OptIn(DelicateApi::class)
@Composable
@Page
fun TermsOfServicePage() {
    val breakpoint = rememberBreakpoint()
    val largeBreakpoint = remember(breakpoint) {
        breakpoint > Breakpoint.LG
    }

    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .backgroundImage(BackgroundGradient)
                .padding(
                    leftRight = if (breakpoint > Breakpoint.LG) 120.px
                    else if (breakpoint == Breakpoint.LG) 100.px
                    else if (breakpoint > Breakpoint.MD) 70.px
                    else if (breakpoint == Breakpoint.MD) 50.px
                    else 40.px
                ),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(if (largeBreakpoint) 800.px else 380.px)
                    .padding(bottom = 100.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Main Title
                H1(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 20.px, top = if (largeBreakpoint) 50.px else 20.px)
                        .backgroundImage(TextGradient)
                        .background { this.clip(BackgroundClip.Text) }
                        .color(Color.transparent)
                        .fontFamily(ACME_REGULAR)
                        .fontSize(if (largeBreakpoint) 4.cssRem else 3.cssRem)
                        .lineHeight(1.2)
                        .textAlign(TextAlign.Center)
                        .toAttrs()
                ) {
                    Text("Terms of Service")
                }

                // Subtitle Information
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 40.px)
                        .color(Theme.TextLowVisibility.rgb)
                        .fontFamily(LATO_REGULAR)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.4)
                        .textAlign(TextAlign.Center)
                        .toAttrs()
                ) {
                    Text("Effective Date: 26.10.2025")
                }

                Surface(modifier = Modifier.height(20.px)) {}

                // Introduction
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 40.px)
                        .color(Theme.Text.rgb)
                        .fontFamily(LATO_REGULAR)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.6)
                        .textAlign(TextAlign.Start)
                        .toAttrs()
                ) {
                    Text("Welcome to Social Creatives Kit! These Terms of Service (\"Terms\") govern your use of the Social Creatives Kit mobile application (\"App\"), operated by STIV Apps (\"we,\" \"our,\" or \"us\"). By downloading, accessing, or using Social Creatives Kit, you agree to these Terms. If you do not agree, please do not use the App.")
                }

                // Section 1
                SectionTitle("1. Use of the App", largeBreakpoint)
                SectionContent("You may use Social Creatives Kit only in accordance with these Terms and all applicable laws.")
                SectionContent("You agree not to:")
                SectionContent("• Use the App for any illegal, harmful, or unauthorized purpose.")
                SectionContent("• Upload, share, or process content that violates any laws, infringes copyrights, or includes explicit or offensive material.")
                SectionContent("• Attempt to reverse engineer, modify, or copy any part of the App's source code or design.")
                SectionContent("• Interfere with or disrupt the operation of the App or its servers.")

                // Section 2
                SectionTitle("2. User Accounts", largeBreakpoint)
                SectionContent("To access certain features, you may need to create an account or sign in using a third-party provider (e.g., Google or Apple).")
                SectionContent("You agree to:")
                SectionContent("• Provide accurate and complete information during registration.")
                SectionContent("• Keep your login credentials secure and confidential.")
                SectionContent("• Be responsible for all activities that occur under your account.")
                SectionContent("We reserve the right to suspend or terminate your account if we believe you have violated these Terms.")

                // Section 3
                SectionTitle("3. Uploaded Images and Content", largeBreakpoint)
                SectionContent("When you upload or edit photos in Social Creatives Kit:")
                SectionContent("• You retain ownership of your content.")
                SectionContent("• You grant us a limited, non-exclusive license to process and temporarily store your images for the purpose of providing app features (e.g., background removal, outline effects, or AI edits).")
                SectionContent("• You may delete your content at any time, and it will be permanently removed from our servers.")
                SectionContent("• You are responsible for ensuring that any content you upload complies with applicable laws and does not infringe on the rights of others.")

                // Section 4
                SectionTitle("4. In-App Purchases", largeBreakpoint)
                SectionContent("Social Creatives Kit may offer in-app purchases or credit-based features.")
                SectionContent("• Prices and available features are subject to change without notice.")
                SectionContent("• All purchases are handled through official app stores (Google Play or Apple App Store) and are subject to their respective terms and refund policies.")
                SectionContent("• You are responsible for any applicable taxes or fees related to your purchases.")

                // Section 5
                SectionTitle("5. Intellectual Property", largeBreakpoint)
                SectionContent("All intellectual property rights in Social Creatives Kit — including its name, logo, user interface, design, and underlying code — are owned by STIV Apps.")
                SectionContent("You may not copy, modify, distribute, or create derivative works from any part of the App without our prior written consent.")

                // Section 6
                SectionTitle("6. Termination", largeBreakpoint)
                SectionContent("We may suspend or terminate your access to Social Creatives Kit at any time, with or without notice, if you violate these Terms or if required by law.")
                SectionContent("You may also stop using the App at any time.")
                SectionContent("Upon termination, all rights granted to you under these Terms will end immediately.")

                // Section 7
                SectionTitle("7. Disclaimer of Warranties", largeBreakpoint)
                SectionContent("Social Creatives Kit is provided \"as is\" and \"as available.\"")
                SectionContent("We make no warranties, express or implied, about:")
                SectionContent("• The accuracy, reliability, or availability of the App.")
                SectionContent("• The uninterrupted or error-free operation of the App.")
                SectionContent("• The suitability of the App for your particular purpose.")
                SectionContent("Your use of the App is at your own risk.")

                // Section 8
                SectionTitle("8. Limitation of Liability", largeBreakpoint)
                SectionContent("To the fullest extent permitted by law, STIV Apps and its affiliates shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the App, including loss of data, profits, or reputation.")
                SectionContent("If any liability is found, it will be limited to the amount you paid (if any) for using the App in the past 12 months.")

                // Section 9
                SectionTitle("9. Changes to These Terms", largeBreakpoint)
                SectionContent("We may update or modify these Terms from time to time.")
                SectionContent("Changes will take effect when they are posted, and the updated \"Effective Date\" will be shown at the top.")
                SectionContent("Your continued use of Social Creatives Kit after any changes means you accept the updated Terms.")

                // Section 10
                SectionTitle("10. Contact Us", largeBreakpoint)
                SectionContent("If you have any questions about these Terms, please contact us:")
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(topBottom = 10.px)
                        .color(Theme.Text.rgb)
                        .fontFamily(LATO_REGULAR)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.6)
                        .textAlign(TextAlign.Start)
                        .toAttrs()
                ) {
                    Text(SUPPORT_EMAIL)
                }
            }
        }
    }
}

@Composable
private fun SectionTitle(
    text: String,
    largeBreakpoint: Boolean,
) {
    Surface(modifier = Modifier.height(30.px)) {}
    H2(
        attrs = TextSelectionStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 20.px)
            .color(Theme.Text.rgb)
            .fontFamily(LATO_LIGHT)
            .fontSize(if (largeBreakpoint) 1.8.cssRem else 1.6.cssRem)
            .lineHeight(1.3)
            .textAlign(TextAlign.Start)
            .toAttrs()
    ) {
        Text(text)
    }
}

@Composable
private fun SectionContent(
    text: String,
) {
    P(
        attrs = TextSelectionStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 15.px)
            .color(Theme.TextLowVisibility.rgb)
            .fontFamily(LATO_LIGHT)
            .fontSize(1.1.cssRem)
            .lineHeight(1.6)
            .textAlign(TextAlign.Start)
            .toAttrs()
    ) {
        Text(text)
    }
}